import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import InstrumentationCheck from "@/components/admin/InstrumentationCheck";

const Tile = ({ label, value, sub, warn, testid }) => (
    <div className={`ml-card p-4 ${warn ? "!border-coral-softer bg-coral-light/40" : ""}`} data-testid={testid}>
        <div className="font-heading font-bold text-2xl text-[#12776F]">{value ?? "—"}</div>
        <div className="text-sm font-medium text-[#1F2624]">{label}</div>
        {sub && <div className="text-xs text-[#6E7A74] mt-0.5">{sub}</div>}
    </div>
);

export default function DataQuality() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get("/admin/os/data-quality").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Failed to load"));
    }, []);

    if (err) return <div className="ml-card p-6 text-coral-dark">{err}</div>;
    if (!data) return <div className="ml-card p-8 flex items-center gap-2 text-[#6E7A74]" data-testid="dataq-loading"><Loader2 size={16} className="animate-spin" /> Auditing data quality…</div>;

    return (
        <div className="space-y-5" data-testid="data-quality">
            <div>
                <h2 className="font-heading font-bold text-2xl text-[#12776F] flex items-center gap-2"><ShieldCheck size={20} className="text-teal-dark" /> Data quality — trust every dashboard</h2>
                <p className="text-sm text-[#6E7A74]">{data.excluded.note}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Tile label="Real caregivers counted" value={data.real_users} testid="dq-real" />
                <Tile label="Admin users excluded" value={data.excluded.admin_users} testid="dq-admins" />
                <Tile label="QA / tester users excluded" value={data.excluded.qa_tester_users} testid="dq-testers" />
                <Tile label="Internal-domain users excluded" value={data.excluded.internal_domain_users} testid="dq-internal" />
                <Tile label="Duplicate emails" value={data.duplicate_emails.count} warn={data.duplicate_emails.count > 0}
                    sub={data.duplicate_emails.examples?.join(", ")} testid="dq-dupes" />
                <Tile label="Unknown users" value={data.unknown_users.count} warn={data.unknown_users.count > 0}
                    sub={data.unknown_users.definition} testid="dq-unknown" />
                <Tile label="Silent events (28d)" value={data.missing_events_28d?.length ?? 0}
                    warn={(data.missing_events_28d?.length ?? 0) > 0} sub={(data.missing_events_28d || []).slice(0, 4).join(", ")} testid="dq-missing" />
                <Tile label="Domain watchdog" value={data.integrations.domain_watch_status}
                    warn={data.integrations.domain_watch_status === "down"} testid="dq-watchdog" />
            </div>

            <div className="ml-card p-5" data-testid="dq-integrations">
                <h3 className="font-heading font-semibold text-lg text-[#1F2624]">Tracking integrations</h3>
                <div className="mt-2 flex flex-wrap gap-2 text-sm">
                    {["ga4", "gsc", "clarity", "resend"].map((k) => (
                        <span key={k} className={`px-3 py-1 rounded-2xl text-xs font-semibold ${data.integrations[k] ? "bg-sage-light text-sage-dark" : "bg-coral-light text-coral-dark"}`}>
                            {k.toUpperCase()}: {data.integrations[k] ? "configured" : "missing"}
                        </span>
                    ))}
                </div>
            </div>

            <div className="ml-card p-5 overflow-x-auto" data-testid="dq-taxonomy">
                <h3 className="font-heading font-semibold text-lg text-[#1F2624]">Event taxonomy (canonical → legacy aliases)</h3>
                <p className="text-xs text-[#6E7A74] mb-2">Legacy event names keep firing unchanged — historical reports stay intact. Canonical names are the reporting layer.</p>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                            <th className="py-2 pr-3">Group</th><th className="py-2 pr-3">Canonical event</th><th className="py-2 pr-3">Legacy aliases</th><th className="py-2">Fired (28d)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.event_taxonomy.map((t) => (
                            <tr key={t.canonical} className="border-b border-cream-light last:border-0">
                                <td className="py-1.5 pr-3 text-[#6E7A74]">{t.group}</td>
                                <td className="py-1.5 pr-3 font-mono text-xs font-semibold text-[#1F2624]">{t.canonical}</td>
                                <td className="py-1.5 pr-3 font-mono text-xs text-[#4C5551]">{t.aliases.join(", ")}</td>
                                <td className="py-1.5">
                                    {t.fired_28d === null ? <span className="text-[#6E7A74]">n/a</span>
                                        : t.fired_28d ? <span className="text-sage-dark font-semibold">yes</span>
                                            : <span className="text-coral-dark font-semibold inline-flex items-center gap-1"><AlertTriangle size={12} /> no</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <InstrumentationCheck />
        </div>
    );
}
